import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Sparkle } from "@phosphor-icons/react";

/**
 * TermsReacceptGate — sits inside the authenticated AppLayout.
 *
 * On mount it calls GET /api/legal/terms/me/status. If
 * `needs_reaccept=true` (because we published a new version of the
 * terms OR the user never confirmed the WhatsApp opt-in), it shows
 * a non-dismissible modal that blocks every action until the user
 * accepts.
 *
 * Compliance rationale:
 *  - Bumping CURRENT_TERMS_VERSION must produce a hard, auditable
 *    re-consent — not a silent banner the user can ignore.
 *  - The WhatsApp opt-in declaration is the most legally exposed
 *    clause; we capture it AGAIN on every version bump so the proof
 *    chain stays fresh.
 *  - The accept POST returns the new timestamp + IP captured server-
 *    side, which lands in audit_logs.
 */
export default function TermsReacceptGate() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [acceptChecked, setAcceptChecked] = useState(false);

  // Check status on mount + every page focus (in case the user already
  // accepted in another tab).
  useEffect(() => {
    if (!user) return;
    let alive = true;
    const check = () => {
      api.get("/legal/terms/me/status")
        .then((r) => { if (alive) setStatus(r.data); })
        .catch(() => { /* fail-open: don't block the app on transient errors */ });
    };
    check();
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    return () => { alive = false; window.removeEventListener("focus", onFocus); };
  }, [user]);

  if (!user || !status || !status.needs_reaccept) return null;

  const isFirstAccept = !status.user_version;

  const handleAccept = async () => {
    if (!acceptChecked) {
      toast.error("Marque a caixa de aceite para continuar.");
      return;
    }
    setAccepting(true);
    try {
      await api.post("/legal/terms/accept", {
        terms_version: status.current_version,
        // Cláusula 7 está implícita no aceite — backend exige true.
        confirm_whatsapp_optin: true,
      });
      toast.success("Termos aceitos.");
      setStatus({ ...status, needs_reaccept: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao registrar aceite.");
    } finally {
      setAccepting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="terms-reaccept-modal"
    >
      <div className="w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-slate-200/70 overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <header className="px-6 pt-6 pb-4 border-b border-slate-100">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="h-7 w-7 rounded-full bg-slate-900 text-white flex items-center justify-center">
              <Sparkle size={12} weight="fill" />
            </div>
            <span className="text-[10.5px] uppercase tracking-[0.08em] font-semibold text-slate-500">
              Termos atualizados · v{status.current_version}
            </span>
          </div>
          <h2 className="text-[20px] font-semibold tracking-tight text-slate-900 leading-tight">
            {isFirstAccept
              ? "Antes de continuar — aceite os Termos"
              : "Atualizamos os Termos de Uso"}
          </h2>
          <p className="text-[13px] text-slate-600 mt-1.5 leading-relaxed">
            {isFirstAccept
              ? "Sua conta foi criada antes desta versão. Precisamos do seu aceite para você seguir."
              : "Publicamos uma nova versão. Leia e aceite para continuar usando o AgentUS."}
          </p>
        </header>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1">
          <p className="text-[13px] text-slate-700 leading-relaxed mb-4">
            Para continuar usando o AgentUS, leia e aceite os{" "}
            <Link
              to="/legal/termos"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-900 font-medium underline underline-offset-[3px] decoration-slate-400 hover:decoration-slate-900"
              data-testid="terms-reaccept-link"
            >
              Termos de Uso e Política de Privacidade (versão {status.current_version})
            </Link>
            .
          </p>

          <label
            className="flex items-start gap-2.5 cursor-pointer p-3 rounded-xl border border-slate-200 bg-slate-50/50 hover:border-slate-900 transition-colors"
            data-testid="terms-reaccept-checkbox-label"
          >
            <input
              type="checkbox"
              checked={acceptChecked}
              onChange={(e) => setAcceptChecked(e.target.checked)}
              data-testid="terms-reaccept-checkbox"
              className="mt-[3px] h-3.5 w-3.5 rounded border-slate-300 text-slate-900 focus:ring-1 focus:ring-slate-900 focus:ring-offset-0 shrink-0 cursor-pointer"
            />
            <span className="text-[12.5px] leading-[1.55] text-slate-700">
              Li e aceito os Termos de Uso e Política de Privacidade.
            </span>
          </label>
        </div>

        {/* Footer */}
        <footer className="px-6 py-4 border-t border-slate-100 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 bg-slate-50/50">
          <span className="text-[11px] text-slate-500 leading-snug">
            Você precisa aceitar para continuar.<br className="hidden sm:block" />
            Não é possível pular esta etapa.
          </span>
          <button
            type="button"
            disabled={accepting || !acceptChecked}
            onClick={handleAccept}
            data-testid="terms-reaccept-submit"
            className="h-10 px-5 rounded-xl bg-slate-900 text-white text-[13px] font-medium tracking-tight hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {accepting ? (
              <>
                <span className="w-3 h-3 rounded-full border-[1.5px] border-white/30 border-t-white animate-spin" />
                Registrando…
              </>
            ) : (
              "Aceitar e continuar"
            )}
          </button>
        </footer>
      </div>
    </div>
  );
}
